import { useState } from 'react'
import { FileText, Loader2 } from 'lucide-react'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { cn } from '@/lib/utils'
import { formatMoney } from '@/lib/poTotals'
import { usePurchaseOrders } from '@/hooks/usePurchaseOrders'
import type { PurchaseOrderFilter } from '@/types/purchaseOrders'
import { PoStatusBadge, formatPoDate } from './poStatus'
import { PurchaseOrderDetailDialog } from './PurchaseOrderDetailDialog'

const FILTERS: { value: PurchaseOrderFilter; label: string }[] = [
  { value: 'open', label: 'Open' },
  { value: 'draft', label: 'Draft' },
  { value: 'sent', label: 'Sent' },
  { value: 'received', label: 'Received' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'all', label: 'All' },
]

const HEADERS = ['Number', 'Supplier', 'Status', 'Rev', 'Lines', 'Grand total', 'Requested delivery', 'Created']

/** Re-Order → Purchase orders: track what has been ordered (z8nrz7f7dz). */
export function PurchaseOrdersList({ search }: { search: string }) {
  const [status, setStatus] = useState<PurchaseOrderFilter>('open')
  const [openId, setOpenId] = useState<string | null>(null)
  const { data, isLoading, error } = usePurchaseOrders({ status, q: search.trim() || undefined })
  const rows = data?.items ?? []

  return (
    <div className='space-y-3'>
      <div className='flex flex-wrap gap-1 rounded-lg bg-muted p-1' role='group' aria-label='Purchase order status'>
        {FILTERS.map((f) => (
          <button
            key={f.value}
            type='button'
            aria-pressed={status === f.value}
            onClick={() => setStatus(f.value)}
            className={cn(
              'rounded-md px-3 py-1.5 text-xs font-medium transition-colors',
              status === f.value ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900',
            )}
          >
            {f.label}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className='flex items-center gap-2 p-8 text-sm text-muted-foreground'>
          <Loader2 className='h-4 w-4 animate-spin' />
          Loading purchase orders…
        </div>
      ) : error ? (
        <p className='py-12 text-center text-sm text-red-600'>
          {error instanceof Error ? error.message : 'Could not load purchase orders.'}
        </p>
      ) : rows.length === 0 ? (
        <div className='py-10 text-center'>
          <FileText className='mx-auto h-6 w-6 text-muted-foreground' />
          <p className='mt-2 text-sm text-muted-foreground'>No purchase orders here.</p>
        </div>
      ) : (
        <div className='overflow-x-auto'>
          <Table className='min-w-[900px]'>
            <TableHeader>
              <TableRow>
                {HEADERS.map((h) => (
                  <TableHead key={h} className='whitespace-nowrap'>
                    {h}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((po, i) => (
                <TableRow
                  key={po.id}
                  role='link'
                  tabIndex={0}
                  onClick={() => setOpenId(po.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      setOpenId(po.id)
                    }
                  }}
                  className={cn(
                    'cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset',
                    i % 2 === 1 && 'bg-muted/10',
                  )}
                >
                  <TableCell className='font-mono text-xs font-semibold text-gray-900'>{po.number}</TableCell>
                  <TableCell>{po.supplierName}</TableCell>
                  <TableCell>
                    <PoStatusBadge status={po.status} />
                  </TableCell>
                  <TableCell className='tabular-nums text-gray-600'>Rev {po.revision}</TableCell>
                  <TableCell className='tabular-nums'>{po.lineCount}</TableCell>
                  <TableCell className='whitespace-nowrap font-medium tabular-nums'>
                    {formatMoney(po.grandTotal, po.currency)}
                  </TableCell>
                  <TableCell className='whitespace-nowrap'>{formatPoDate(po.requestedDeliveryDate)}</TableCell>
                  <TableCell className='whitespace-nowrap text-gray-600'>{formatPoDate(po.createdAt)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <PurchaseOrderDetailDialog poId={openId} onClose={() => setOpenId(null)} />
    </div>
  )
}
